<template>
  <BModal
    id="generic-ts-form"
    ref="genericTSModal"
    :title="translatedLabel('formTitle', 'mymachine_GenericTSForm')"
    no-close-on-backdrop
    @hide="resetForm"
  >
    <div class="px-3 px-md-5">
      <p>{{ translate('formDescription', labels, 'mymachine_GenericTSForm') }}</p>
      <div class="d-flex justify-content-center">
        <div class="w-100">
          <BFormGroup
            id="model-group"
            label-for="product-model"
            aria-describedby="model-feedback"
          >
            <input
              v-model="productModel"
              class="d-none"
              type="text"
              name="model"
            >
            <BasicFilterItem
              id="product-model"
              ref="productModelRef"
              class="wide dropdown m-0"
              :disabled="loading || products.length === 1"
              :title="selectedProductModel"
              :class="{'is-invalid': false, 'is-placeholder': productModel === ''}"
            >
              <div class="p-1" />
              <div
                v-for="(itm, i) in products"
                :key="'products_item_' + i"
                class="nav-item text-nowrap"
                :class="{'disabled': isDisabledOption(itm)}"
                @click="() => { productModelRef?.hideDropdown() }"
              >
                <BDropdownItem @click="select(itm)">{{ itm }}</BDropdownItem>
              </div>
            </BasicFilterItem>
          </BFormGroup>
          <BFormGroup
            id="code-group"
            label-for="code"
            aria-describedby="code-feedback"
          >
            <BFormInput
              id="code"
              v-model="code"
              name="email"
              :disabled="productModel === ''"
              :placeholder="translatedLabel('codePlaceholder', 'mymachine_GenericTSForm')"
              trim
              @blur="codeState = code !== ''"
              @keydown.enter="onSubmit"
            />
            <BFormInvalidFeedback
              id="code-feedback"
              :state="codeState"
            >
              <b-icon
                icon="exclamation-triangle"
                class="me-1"
              />{{ translate('codeRequired', labels, 'mymachine_GenericTSForm') }}
            </BFormInvalidFeedback>
          </BFormGroup>
        </div>
      </div>

      <b-notification
        v-if="error.present && error.code === 404"
        data-test-id="no-items"
      >
        {{ translate('notFound', labels, 'mymachine_GenericTSForm') }}
      </b-notification>
      <b-error
        v-else-if="error.present"
        :code="Number(error.code)"
        data-test-id="maintenance-error"
      >
        {{ translate('defaultError', labels, 'shared_CommonErrors') }}
      </b-error>
      <template v-else-if="result.code">
        <BasicGridTable
          grid-name="faultCodeDetails"
          class="mb-4"
        >
          <template #header>
            <BasicGridTableHeader name="code">{{ translate('codeTableHeader', labels, 'mymachine_GenericTSForm') }}</BasicGridTableHeader>
            <BasicGridTableHeader name="description">{{ translate('descriptionTableHeader', labels, 'mymachine_GenericTSForm') }}</BasicGridTableHeader>
          </template>
          <template #rows>
            <div class="grid-table-row overflow-hidden">
              <div class="item__code"><span class="font-condensed">{{ result.code }}</span></div>
              <div class="item__description">{{ result.description }}</div>
            </div>
          </template>
        </BasicGridTable>
        <b-notification>
          {{ translate('notificationMessage', labels, 'mymachine_GenericTSForm') }}
        </b-notification>
      </template>
    </div>
    <template #footer>
      <div class="text-end d-flex flex-wrap d-md-block w-100">
        <BButton
          class="mt-2 ms-md-3 order-1 col-12 col-md-auto"
          variant="outline-primary"
          @click="closeModal"
        >
          {{ translate('closeButton', labels, 'shared_CommonFunctional') }}
        </BButton>
        <BButton
          type="submit"
          :disabled="productModel === '' || code === ''"
          variant="primary"
          class="mt-2 ms-md-3 order-0 col-12 col-md-auto d-inline-flex justify-content-center align-items-center"
          @click="onSubmit"
        >
          <BSpinner
            v-if="loading"
            small
            class="me-2"
          />{{ translate('submitButton', labels, 'mymachine_GenericTSForm') }}<b-icon
            icon="bobcaticon-create"
            class="small ms-1"
          />
        </BButton>
      </div>
    </template>
  </BModal>
</template>

<script setup lang="ts">
import {computed, ref} from 'vue'
import {useStore} from 'vuex'
import BasicFilterItem from '@/components/ui/forms/BasicFilterItem.vue'
import BasicGridTable from '@/components/ui/BasicGridTable.vue'
import BasicGridTableHeader from '@/components/ui/BasicGridTableHeader.vue'
import BNotification from '@/components/ui/BNotification.vue'
import BError from '@/components/ui/BError.vue'
import {dpmmService} from '@/services'
import {useCommon} from '@/composables/useCommon'

withDefaults(defineProps<{
  disabled?: boolean
}>(), {
  disabled: false
})

const {labels, translate, translatedLabel} = useCommon()
const store = useStore()

const genericTSModal = ref<any>(null)
const productModelRef = ref<any>(null)

const productModel = ref('')
const code = ref('')
const codeState = ref(true)
const result = ref<Record<string, any>>({})
const error = ref<{ present: boolean; code: string | number }>({present: false, code: ''})
const loading = ref(false)

const getLanguage = computed<string>(() => store.getters['mymachine/getLanguage'])

const products = computed<string[]>(() => {
  return labels.value?.['mymachine_GenericTSForm']?.productModelValues?.split('\n').filter((el: string) => el !== '') || []
})

const selectedProductModel = computed<string>(() => {
  return productModel.value || translatedLabel('productModelPlaceholder', 'mymachine_GenericTSForm')
})

function onSubmit(): void {
  error.value = {present: false, code: ''}
  result.value = {}
  loading.value = true
  dpmmService.fetchFaultCodesDetails(code.value, productModel.value, getLanguage.value).then((response: any) => {
    result.value = response.data
    loading.value = false
  }).catch((r: any) => {
    loading.value = false
    error.value = {present: true, code: r.code}
  })
}

function openModal(): void {
  genericTSModal.value?.show()
}

function select(i: string): void {
  productModel.value = i
}

function resetForm(): void {
  productModel.value = ''
  code.value = ''
  result.value = {}
  error.value = {present: false, code: ''}
}

function closeModal(): void {
  genericTSModal.value?.hide()
}

function isDisabledOption(item: unknown): boolean {
  return typeof item === 'object' && item !== null && 'disabled' in item && !!(item as Record<string, unknown>).disabled
}

defineExpose({openModal})
</script>
